import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { KnowledgeDocumentEntry } from 'types'

import { compareRevisionCodes } from './revisionHelpers'

/** Page to land on when no page-level metadata is available. */
export const DEFAULT_TARGET_PAGE = 1

/**
 * How a reference resolves to its target document(s). `LegacyStringMatch` (the
 * default) matches the reference text against system-document `title` /
 * `drawing_number` and must stay the default — existing sites rely on it.
 */
export enum ReferenceResolverStrategy {
  LegacyStringMatch = 'legacy-string-match',
  IdBased = 'id-based',
}

/** Document to open, the page to land on, and its revision (for latest-revision picks). */
export type NavigationTargetCandidate = {
  documentId: string
  pageNumber: number
  revisionCode?: string
}

/**
 * Page to land on for a unit-tag reference: the earliest page the unit appears on
 * in the target document (`data.fields.page_number`), or page 1 if unknown.
 */
export function resolveKnowledgeDocumentTargetPage(
  knowledgeDocuments: KnowledgeDocumentEntry[],
  systemDocumentId: string
): number {
  const pages = knowledgeDocuments
    .filter((doc) => doc.entry.tags['system-document'] === systemDocumentId)
    .map((doc) => Number(doc.entry.data.fields.page_number))
    .filter((page) => Number.isFinite(page) && page >= 1)

  return pages.length > 0 ? Math.min(...pages) : DEFAULT_TARGET_PAGE
}

export type OffPageConnectorResolverInput = {
  strategy?: ReferenceResolverStrategy
  /** Legacy input: system-document entries matched by string (`title` / `drawing_number`). */
  offPageConnectorDocuments: SystemDocumentEntry[]
  /**
   * Id-based input: targets resolved by id. Empty until `doc_reference` subjects are
   * plumbed in; the `IdBased` branch already consumes them. See H2 plan T3.
   */
  docReferenceTargets?: NavigationTargetCandidate[]
}

/** Resolve candidate targets for an OPC / drawing-number reference per the strategy. */
export function resolveOffPageConnectorTargets({
  strategy = ReferenceResolverStrategy.LegacyStringMatch,
  offPageConnectorDocuments,
  docReferenceTargets = [],
}: OffPageConnectorResolverInput): NavigationTargetCandidate[] {
  if (strategy === ReferenceResolverStrategy.IdBased) {
    return docReferenceTargets
  }

  return offPageConnectorDocuments.map((document) => ({
    documentId: document.entry.id,
    // Page 1: string matches are document-level only, so a match can't be
    // narrowed to a specific sheet. Resolve the sheet here once documents
    // carry per-page drawing numbers.
    pageNumber: DEFAULT_TARGET_PAGE,
    revisionCode: document.entry.data.revision_code,
  }))
}

export function allCandidatesHaveRevisionCodes(
  candidates: NavigationTargetCandidate[]
): boolean {
  return candidates.every(
    (candidate) =>
      candidate.revisionCode !== undefined &&
      candidate.revisionCode !== null &&
      candidate.revisionCode !== ''
  )
}

export function getLatestCandidateByRevision(
  candidates: NavigationTargetCandidate[]
): NavigationTargetCandidate | undefined {
  if (candidates.length === 0) return undefined
  const sortedByRevision = [...candidates].sort(
    (a, b) => -compareRevisionCodes(a.revisionCode ?? '', b.revisionCode ?? '')
  )
  return sortedByRevision[0]
}
